import React, { useMemo } from "react";

export type SocialPlatformFilter = "all" | "meta_ig" | "meta_fb" | "gbp";

interface Props {
  activePlatform: SocialPlatformFilter;
  onChange: (platform: SocialPlatformFilter) => void;
  availablePlatforms?: SocialPlatformFilter[];
}

export const PLATFORM_LABELS: Record<SocialPlatformFilter, string> = {
  all: "Visión Global",
  meta_ig: "Instagram",
  meta_fb: "Facebook",
  gbp: "Google BP",
};

const PLATFORMS: Array<{ id: SocialPlatformFilter; label: string; color: string; short: string }> = [
  { id: "all", label: "Visión Global", color: "var(--blue, #7d96ff)", short: "ALL" },
  { id: "meta_ig", label: "Instagram", color: "#e1306c", short: "IG" },
  { id: "meta_fb", label: "Facebook", color: "#1877f2", short: "FB" },
  { id: "gbp", label: "Google BP", color: "#34a853", short: "GBP" },
];

export function SocialPlatformSelector({ activePlatform, onChange, availablePlatforms }: Props) {
  const visiblePlatforms = useMemo(() => {
    if (!availablePlatforms || availablePlatforms.length === 0) return PLATFORMS;
    // Si solo hay 1 red disponible para este cliente, mostrar únicamente esa red
    if (availablePlatforms.length === 1) {
      return PLATFORMS.filter((p) => p.id === availablePlatforms[0]);
    }
    // Si hay 2 o más, mostrar 'Visión Global' más las redes que el cliente efectivamente tiene
    return PLATFORMS.filter((p) => p.id === "all" || availablePlatforms.includes(p.id));
  }, [availablePlatforms]);

  return (
    <div
      className="social-platform-selector"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        alignItems: "center",
        padding: "6px 8px",
        background: "var(--surface, #11141b)",
        border: "1px solid var(--line, rgba(255, 255, 255, 0.08))",
        borderRadius: "12px",
        marginBottom: "16px",
      }}
    >
      <span
        style={{
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--dim, #5e6677)",
          padding: "0 8px",
        }}
      >
        Plataforma
      </span>
      {visiblePlatforms.map((p) => {
        const isActive = activePlatform === p.id;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onChange(p.id)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "7px 14px",
              borderRadius: "8px",
              border: isActive
                ? `1px solid ${p.color}`
                : "1px solid var(--line, rgba(255, 255, 255, 0.08))",
              background: isActive
                ? `rgba(255, 255, 255, 0.06)`
                : "transparent",
              color: isActive ? "var(--text, #f6f7fb)" : "var(--muted, #8e96a8)",
              fontSize: "13px",
              fontWeight: isActive ? 650 : 500,
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: p.color,
                boxShadow: isActive ? `0 0 8px ${p.color}` : "none",
              }}
            />
            {p.label}
          </button>
        );
      })}
    </div>
  );
}
